// components/ui/MarkdownRenderer.tsx
// FULL REPLACEMENT — adds auto-generated IDs on H1/H2/H3 for TOC jump links.
// Changes vs original are marked with // ✅ CHANGED

import React from 'react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

// ✅ CHANGED — shared slugify so StickyTOC + renderer produce identical IDs
const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {

  const renderMarkdown = (text: string) => {
    if (!text) return '';

    let html = text;

    // 1. Escape HTML
    html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    // 2. Code Blocks
    const codeBlocks: string[] = [];
    html = html.replace(/```([\s\S]*?)```/g, (_match, code) => {
      codeBlocks.push(
        `<pre class="bg-slate-900 text-slate-50 p-4 rounded-lg overflow-x-auto mb-6 font-mono text-sm"><code>${code.trim()}</code></pre>`
      );
      return `__CODEBLOCK_${codeBlocks.length - 1}__`;
    });

    // 3. Inline Code
    html = html.replace(
      /`([^`]+)`/g,
      '<code class="bg-slate-100 text-pink-600 rounded px-1.5 py-0.5 font-mono text-sm">$1</code>'
    );

    // 4. Images
    html = html.replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      '<img src="$2" alt="$1" class="rounded-lg shadow-md max-w-full my-6 border border-slate-200" />'
    );

    // 5. Headers — ✅ CHANGED: now generate id + scroll-mt-24
    html = html.replace(/^### (.*$)/gm, (_m: string, t: string) => {
      const id = slugify(t);
      return `<h3 id="${id}" class="text-xl font-bold text-brand-dark mt-8 mb-4 scroll-mt-24">${t}</h3>`;
    });
    html = html.replace(/^## (.*$)/gm, (_m: string, t: string) => {
      const id = slugify(t);
      return `<h2 id="${id}" class="text-2xl font-bold text-brand-dark mt-10 mb-5 pb-2 border-b border-slate-100 scroll-mt-24">${t}</h2>`;
    });
    html = html.replace(/^# (.*$)/gm, (_m: string, t: string) => {
      const id = slugify(t);
      return `<h1 id="${id}" class="text-3xl font-bold text-brand-dark mt-12 mb-6 scroll-mt-24">${t}</h1>`;
    });

    // 6. Horizontal Rules
    html = html.replace(/^---$/gm, '<hr class="my-8 border-slate-200" />');

    // 7. Blockquotes
    html = html.replace(
      /^\> (.*$)/gm,
      '<blockquote class="border-l-4 border-brand-blue pl-4 italic text-slate-700 my-6">$1</blockquote>'
    );

    // 8. Bold, Italic, Strikethrough
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/~~(.*?)~~/g, '<del class="text-slate-400">$1</del>');

    // 9. Links
    html = html.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-brand-blue hover:underline">$1</a>'
    );

    // 10. Tables
    const tableRegex = /\|(.+)\|\n\|([-:| ]+)\|\n((?:\|.*\|\n?)+)/g;
    html = html.replace(tableRegex, (_match, headerRow, _sep, bodyRows) => {
      const headers = headerRow
        .split('|')
        .filter((c: string) => c.trim())
        .map(
          (c: string) =>
            `<th class="border border-slate-300 px-4 py-2 bg-brand-slate font-bold text-left text-slate-700">${c.trim()}</th>`
        )
        .join('');

      const body = bodyRows
        .trim()
        .split('\n')
        .map((row: string) => {
          const cells = row
            .split('|')
            .filter((c: string) => c.trim() !== '')
            .map(
              (c: string) =>
                `<td class="border border-slate-300 px-4 py-2 text-slate-600">${c.trim()}</td>`
            )
            .join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');

      return `<div class="overflow-x-auto my-6"><table class="w-full border-collapse border border-slate-300 text-sm"><thead><tr>${headers}</tr></thead><tbody>${body}</tbody></table></div>`;
    });

    // 11. Lists
    html = html.replace(
      /^\- (.*$)/gm,
      '<li class="ml-4 list-disc pl-1 mb-1 text-slate-700" data-type="ul">$1</li>'
    );
    html = html.replace(
      /^\d+\. (.*$)/gm,
      '<li class="ml-4 list-decimal pl-1 mb-1 text-slate-700" data-type="ol">$1</li>'
    );
    html = html.replace(
      /(<li.*data-type="ul".*<\/li>\s*)+/g,
      '<ul class="mb-6 list-outside">$&</ul>'
    );
    html = html.replace(
      /(<li.*data-type="ol".*<\/li>\s*)+/g,
      '<ol class="mb-6 list-outside">$&</ol>'
    );

    // 12. Paragraphs
    const blockTags = ['h1', 'h2', 'h3', 'ul', 'ol', 'pre', 'blockquote', 'hr', 'div', 'table'];
    const paragraphs = html.split('\n\n');

    html = paragraphs
      .map((p) => {
        if (p.trim() === '') return '';
        const isBlock =
          p.includes('__CODEBLOCK_') ||
          blockTags.some((tag) => p.trim().startsWith(`<${tag}`));
        if (isBlock) return p;
        return `<p class="mb-6 leading-relaxed text-slate-600">${p.trim().replace(/\n/g, '<br/>')}</p>`;
      })
      .join('');

    // 13. Restore Code Blocks
    html = html.replace(/__CODEBLOCK_(\d+)__/g, (_match, index) => codeBlocks[parseInt(index)]);

    return html;
  };

  return (
    <div
      className={`prose prose-slate max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
    />
  );
};